// src/context/SearchContext.js
import React, {createContext, useState, useMemo, useCallback} from 'react';
import api from '../api';

export const SearchContext = createContext();

export const SearchProvider = ({children}) => {
    // States (all original states are preserved)
    const [searchSended, setSearchSended] = useState(false);
    const [searchVendorSended, setSearchVendorSended] = useState(false);
    const [searchLawFirmSended, setSearchLawFirmSended] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const [cityName, setCityName] = useState(null);
    const [stateName, setStateName] = useState(null);

    const [cityForTitle, setCityForTitle] = useState();
    const [companiesForTitle, setCompaniesForTitle] = useState();
    const [searchResults, setSearchResults] = useState([]);
    const [lawFirmResults, setLawFirmResults] = useState([]);
    const [vendorResults, setVendorResults] = useState([]);
    const [vendorFilters, setVendorFilters] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [lawFirmPage, setLawFirmPage] = useState(1);
    const [vendorPage, setVendorPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [lawFirmTotalResults, setLawFirmTotalResults] = useState(0);
    const [vendorTotalResults, setVendorTotalResults] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [lawFirmTotalPages, setLawFirmTotalPages] = useState(1);
    const [vendorTotalPages, setVendorTotalPages] = useState(1);
    const [specialization, setSpecialization] = useState(null);
    const [problem, setProblem] = useState(null);
    const [cities, setCities] = useState([]);
    const [states, setStates] = useState([]);
    const [zipCode, setZipCode] = useState(null);
    const [language, setLanguage] = useState([]);
    const [freeConsultation, setFreeConsultation] = useState(null);
    const [foundationYears, setFoundationYears] = useState([]);
    const [hasAwards, setHasAwards] = useState(false);
    const [contactMethods, setContactMethods] = useState([]);
    const [socialMedia, setSocialMedia] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [suggestion, setSuggestion] = useState('');
    const [testCount, setTestCount] = useState(22);
    const [bannerHeight, setBannerHeight] = useState(0);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [searchSource, setSearchSource] = useState('');

    // Helper function for calculating pages (unchanged)
    const calculateTotalPages = useCallback((count, pageSize = 7) => {
        return Math.ceil(count / pageSize);
    }, []);

    // Original updateSearchResults function (unchanged)
    const updateSearchResults = useCallback(
        ({results, count, page, suggestion}, specializationData) => {
            const pageSize = 7;
            const calculatedTotalPages = calculateTotalPages(count, pageSize);
            setSearchResults((prevResults) => page === 1 ? results : [...prevResults, ...results]);
            if (specializationData) setSpecialization(specializationData);
            setCurrentPage(page);
            setTotalResults(count);
            setTotalPages(calculatedTotalPages);
            setSuggestion(suggestion);
        },
        [calculateTotalPages]
    );

    // Original updateLawFirmResults function (unchanged)
    const updateLawFirmResults = useCallback(
        ({results, count, page, suggestion}) => {
            const pageSize = 7;
            const calculatedTotalPages = calculateTotalPages(count, pageSize);
            setLawFirmResults((prevResults) => page === 1 ? results : [...prevResults, ...results]);
            setLawFirmPage(page);
            setLawFirmTotalResults(count);
            setLawFirmTotalPages(calculatedTotalPages);
            setSuggestion(suggestion);
        },
        [calculateTotalPages]
    );

    // Original updateVendorResults function (unchanged)
    const updateVendorResults = useCallback(
        ({results, count, page, suggestion}) => {
            const pageSize = 7;
            const calculatedTotalPages = calculateTotalPages(count, pageSize);
            setVendorResults((prevResults) => page === 1 ? results : [...prevResults, ...results]);
            setVendorPage(page);
            setVendorTotalResults(count);
            setVendorTotalPages(calculatedTotalPages);
            if (suggestion) {
                setSuggestion(suggestion);
            }
        },
        [calculateTotalPages]
    );

    // >>> NEW FUNCTION TO SET CRITERIA AND SEARCH FOR VENDORS <<<
    const setAndSearchVendors = useCallback(async (criteria) => {
        setLoading(true);
        setSearchVendorSended(true);

        // Step 1: Update context state based on criteria from PageVendors
        setCities(criteria.cities || []);
        setStates(criteria.states || []);
        setZipCode(criteria.zip_codes || null);
        setSpecialization(criteria.service_subtypes || null);
        setLanguage(criteria.languages || []);
        setFoundationYears(criteria.foundation_years || []);
        setFreeConsultation(criteria.workers);
        setServiceTypes(criteria.service_types || []);
        setContactMethods(criteria.contact_methods || []);
        setSocialMedia(criteria.social_media || []);
        setHasAwards(criteria.has_awards || false);

        // Step 2: Build the request body from the passed criteria
        const requestBody = {
            cities: (criteria.cities || []).map(c => ({id: c.city_id, name: c.name})),
            states: (criteria.states || []).map(s => ({state_id: s.state_id, name: s.name})),
            zip_codes: (criteria.zip_codes || []).map(z => ({code: z.code})),
            service_types: (criteria.service_types || []),
            service_subtypes: (criteria.service_subtypes || []),
            foundation_years: (criteria.foundation_years || []).map(y => ({year: y.year})),
            contact_methods: (criteria.contact_methods || []),
            social_media: (criteria.social_media || []),
            languages: (criteria.languages || []).map(l => ({name: l.name})),
            workers: criteria.workers,
            has_awards: criteria.has_awards,
            page: 1, // Always request the first page for a new search
        };

        try {
            setVendorFilters(requestBody);
            const response = await api.post('/vendors-searching/', requestBody);
            updateVendorResults({...response.data, page: 1});
        } catch (error) {
            console.error("Error searching for vendors:", error);
            updateVendorResults({results: [], count: 0, page: 1, suggestion: "An error occurred."});
        } finally {
            setLoading(false);
        }
    }, [
        updateVendorResults,
        setCities,
        setStates,
        setZipCode,
        setSpecialization,
        setLanguage,
        setFoundationYears,
        setFreeConsultation,
        setServiceTypes,
        setContactMethods,
        setSocialMedia,
        setHasAwards,
        setLoading,
        setSearchVendorSended
    ]);

    // Original fetchMoreCompanies function (unchanged)
    const normalizeLanguages = (selectedLanguages) => {
        if (!Array.isArray(selectedLanguages)) {
            return [];
        }
        return selectedLanguages
            .map((lang) => {
                if (!lang) return null;
                if (typeof lang === 'string') return lang;
                if (typeof lang.name === 'string') return lang.name;
                if (lang.name && typeof lang.name.name === 'string') return lang.name.name;
                if (typeof lang.code === 'string') return lang.code;
                return null;
            })
            .filter(Boolean);
    };

    const normalizeFoundationYears = (selectedRanges) => {
        if (!Array.isArray(selectedRanges) || selectedRanges.length === 0) {
            return [];
        }

        // If years are already flattened integers we can reuse them as-is.
        if (selectedRanges.every((year) => typeof year === 'number')) {
            return selectedRanges;
        }

        const currentYear = new Date().getFullYear();
        const yearsSet = new Set();

        selectedRanges.forEach((item) => {
            if (!item) return;

            let range = null;
            if (Array.isArray(item)) {
                range = item;
            } else if (Array.isArray(item.range)) {
                range = item.range;
            } else if (item.year && Array.isArray(item.year.range)) {
                range = item.year.range;
            }

            if (!range || range.length !== 2) {
                return;
            }

            let [start, end] = range;
            const startAge = Number.isFinite(start) ? start : 0;
            let endAge;
            if (!Number.isFinite(end) || end === Number.POSITIVE_INFINITY || end >= Number.MAX_VALUE) {
                endAge = currentYear - 1900;
            } else {
                endAge = end;
            }

            const rangeStartYear = Math.max(1900, currentYear - endAge);
            const rangeEndYear = Math.max(1900, currentYear - startAge);

            for (let year = rangeStartYear; year <= rangeEndYear; year += 1) {
                yearsSet.add(year);
            }
        });

        return Array.from(yearsSet).sort((a, b) => a - b);
    };

    const fetchMoreCompanies = useCallback(async (page) => {
        setLoadingMore(true);
        try {
            const languageFilter = normalizeLanguages(language);
            const foundationYearFilter = normalizeFoundationYears(foundationYears);
            const requestData = {
                specialization: specialization || {id: null, name: null}, problem,
                city_name: cityName, state_name: stateName,
                cities: cities.map((city) => city.city_id),
                states: states.map((st) => st.state_id),
                zip_code: zipCode,
                language: languageFilter,
                free_consultation: freeConsultation,
                foundation_years: foundationYearFilter,
                page,
            };
            const response = await api.post('/get-filtered-specialization/', requestData);
            const {results, count, suggestion} = response.data;
            updateSearchResults({results, count, page, suggestion}, specialization);
        } catch (error) {
            console.error('Error fetching more companies:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [specialization, problem, cityName, stateName, cities, states, zipCode, language, freeConsultation, foundationYears, updateSearchResults]);

    // Original fetchMoreLawFirms function (unchanged)
    const fetchMoreLawFirms = useCallback(async (page) => {
        setLoadingMore(true);
        try {
            const languageFilter = normalizeLanguages(language);
            const foundationYearFilter = normalizeFoundationYears(foundationYears);
            const requestData = {
                specialization,
                cities: cities.map((city) => city.city_id),
                states: states.map((state) => state.state_id),
                zip_code: zipCode,
                language: languageFilter,
                free_consultation: freeConsultation,
                foundation_years: foundationYearFilter,
                page,
            };
            const response = await api.post('/law-firms/', requestData);
            const {results, count} = response.data;
            updateLawFirmResults({results, count, page});
        } catch (error) {
            console.error('Error fetching more law firms:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [specialization, cities, states, zipCode, language, freeConsultation, foundationYears, updateLawFirmResults]);

    // --- CORRECTED "SHOW MORE" FUNCTION FOR VENDORS ---
    const fetchMoreVendors = useCallback(async () => {
        if (loadingMore || vendorResults.length >= vendorTotalResults) return;
        if (!vendorFilters) {
            console.warn('Attempted to load more vendors without stored filters.');
            return;
        }
        setLoadingMore(true);

        const nextPage = vendorPage + 1;

        const requestData = {
            ...vendorFilters,
            page: nextPage,
        };

        try {
            const response = await api.post('/vendors-searching/', requestData);
            updateVendorResults({...response.data, page: nextPage});
        } catch (error) {
            console.error('Error fetching more vendors:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [
        loadingMore, vendorResults.length, vendorTotalResults, vendorPage,
        vendorFilters,
        updateVendorResults
    ]);

    // Original openPopup and closePopup functions (unchanged)
    const openPopup = useCallback((companyId) => {
        const company =
            searchResults.find((c) => c.id === companyId) ||
            lawFirmResults.find((c) => c.id === companyId) ||
            vendorResults.find((c) => c.id === companyId);
        setSelectedCompany(company);
    }, [searchResults, lawFirmResults, vendorResults]);

    const closePopup = useCallback(() => setSelectedCompany(null), []);

    // Original setter functions (unchanged)
    const changeSearchLawFirmSended = useCallback(() => setSearchLawFirmSended(true), []);
    const changeSearchVendorSended = useCallback(() => setSearchVendorSended(true), []);
    const changeSearchSended = useCallback(() => setSearchSended(true), []);
    const changeCityForTitle = useCallback((data) => setCityForTitle(data), []);
    const changeCompaniesForTitle = useCallback((data) => setCompaniesForTitle(data), []);

    // Assemble ALL values into the context provider object
    const contextValue = useMemo(
        () => ({
            // New function
            setAndSearchVendors,

            // All original values and functions
            cityName, setCityName, stateName, setStateName,
            searchVendorSended, changeSearchVendorSended,
            searchLawFirmSended, changeSearchLawFirmSended,
            searchResults, setSearchResults: updateSearchResults,
            lawFirmResults, setLawFirmResults: updateLawFirmResults,
            vendorResults, setVendorResults: updateVendorResults,
            fetchMoreCompanies, fetchMoreLawFirms, fetchMoreVendors,
            openPopup, closePopup, selectedCompany,
            searchSended, changeSearchSended,
            loading, setLoading, loadingMore,
            cityForTitle, changeCityForTitle,
            companiesForTitle, changeCompaniesForTitle,
            currentPage, setCurrentPage,
            lawFirmPage, setLawFirmPage,
            vendorPage, setVendorPage,
            totalResults, lawFirmTotalResults, vendorTotalResults,
            totalPages, lawFirmTotalPages, vendorTotalPages,
            specialization, setSpecialization,
            problem, setProblem,
            cities, setCities,
            states, setStates,
            zipCode, setZipCode,
            language, setLanguage,
            freeConsultation, setFreeConsultation,
            foundationYears, setFoundationYears,
            hasAwards, setHasAwards,
            contactMethods, setContactMethods,
            socialMedia, setSocialMedia,
            serviceTypes, setServiceTypes,
            vendorFilters, setVendorFilters,
            suggestion,
            testCount, bannerHeight, setBannerHeight,
            showAdvanced, setShowAdvanced,
            searchSource, setSearchSource, // Corrected: setSearchSource is now included
            setSearchSended
        }),
        [
            // ALL dependencies are now correctly listed
            setAndSearchVendors,
            cityName, setCityName, stateName, setStateName,
            searchVendorSended, changeSearchVendorSended,
            searchLawFirmSended, changeSearchLawFirmSended,
            searchResults, updateSearchResults,
            lawFirmResults, updateLawFirmResults,
            vendorResults, updateVendorResults,
            fetchMoreCompanies, fetchMoreLawFirms, fetchMoreVendors,
            openPopup, closePopup, selectedCompany,
            searchSended, changeSearchSended,
            loading, setLoading, loadingMore,
            cityForTitle, changeCityForTitle,
            companiesForTitle, changeCompaniesForTitle,
            currentPage, setCurrentPage,
            lawFirmPage, setLawFirmPage,
            vendorPage, setVendorPage,
            totalResults, lawFirmTotalResults, vendorTotalResults,
            totalPages, lawFirmTotalPages, vendorTotalPages,
            specialization, setSpecialization,
            problem, setProblem,
            cities, setCities,
            states, setStates,
            zipCode, setZipCode,
            language, setLanguage,
            freeConsultation, setFreeConsultation,
            foundationYears, setFoundationYears,
            hasAwards, setHasAwards,
            contactMethods, setContactMethods,
            socialMedia, setSocialMedia,
            serviceTypes, setServiceTypes,
            vendorFilters, setVendorFilters,
            suggestion,
            testCount, bannerHeight, setBannerHeight,
            showAdvanced, setShowAdvanced,
            searchSource, setSearchSource, // Corrected: setSearchSource dependency is now included
            setSearchSended // Corrected: setSearchSended dependency is now included
        ]
    );

    return (
        <SearchContext.Provider value={contextValue}>
            {children}
        </SearchContext.Provider>
    );
};
